<template>
  <div class="modal-overlay" @click="close">
    <div class="modal-content" @click.stop>
      <button class="modal-close" @click="close">×</button>

      <div class="modal-header">
        <div class="type-badge" :class="proposal.proposal_type">
          {{ proposal.proposal_type }}
        </div>
        <h2>{{ proposal.title }}</h2>
      </div>

      <div class="modal-body">
        <!-- Description -->
        <div class="section">
          <h3>Description</h3>
          <p>{{ proposal.description }}</p>
        </div>

        <!-- Budget details (only for budget proposals) -->
        <div v-if="isBudget" class="metadata">
          <div class="meta-row">
            <span class="label">Amount:</span>
            <span class="value">{{ formatAmount(budgetData?.amount) }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Purpose:</span>
            <span class="value">{{ budgetData?.purpose || '-' }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Link:</span>
            <span class="value">
              <a v-if="budgetData?.link" :href="budgetData.link" target="_blank" rel="noopener noreferrer">
                {{ budgetData.link }}
              </a>
              <span v-else>-</span>
            </span>
          </div>
        </div>

        <!-- Metadata (hide for budget) -->
        <div v-if="!isBudget" class="metadata">
          <div class="meta-row">
            <span class="label">Created by:</span>
            <span class="value">{{ creatorName }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Status:</span>
            <span class="value status" :class="proposal.status">{{ proposal.status }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Voting Window:</span>
            <span class="value">{{ formatDate(proposal.created_at) }} - {{ formatDate(proposal.voting_ends_at) }}</span>
          </div>
        </div>

        <!-- Vote Tally (hide for budget) -->
        <div v-if="!isBudget" class="section">
          <h3>Vote Tally</h3>
          <div class="vote-tally">
            <div class="tally-item for">
              <span class="tally-label">For</span>
              <span class="tally-count">{{ getOptionCount('for') }}</span>
              <div class="tally-bar">
                <div class="tally-fill for" :style="{ width: getTallyPercentage('for') + '%' }"></div>
                <div class="tally-percent">{{ getTallyPercentage('for') }}%</div>
              </div>
            </div>
            <div class="tally-item against">
              <span class="tally-label">Against</span>
              <span class="tally-count">{{ getOptionCount('against') }}</span>
              <div class="tally-bar">
                <div class="tally-fill against" :style="{ width: getTallyPercentage('against') + '%' }"></div>
                <div class="tally-percent">{{ getTallyPercentage('against') }}%</div>
              </div>
            </div>
            <div class="tally-item abstain">
              <span class="tally-label">Abstain</span>
              <span class="tally-count">{{ getOptionCount('abstain') }}</span>
              <div class="tally-bar">
                <div class="tally-fill abstain" :style="{ width: getTallyPercentage('abstain') + '%' }"></div>
                <div class="tally-percent">{{ getTallyPercentage('abstain') }}%</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Voting Section (hide for budget) -->
        <div v-if="!isBudget && proposal.status === 'active'" class="section voting-section">
          <h3>Cast Your Vote</h3>
          
          <div v-if="hasVoted" class="user-vote-info">
            <p>You voted: <strong>{{ formatVoteOption(votedOption) }}</strong></p>
            <p v-if="userVote?.reason" class="vote-reason">{{ userVote.reason }}</p>
          </div>

          <!-- Optional voter info (public voting) -->
          <div v-if="requireVoterInfo" class="voter-info">
            <div class="field">
              <label>Name</label>
              <input type="text" class="text" v-model="voterName" placeholder="Your name" />
            </div>
            <div class="field">
              <label>Email</label>
              <input type="email" class="text" v-model="voterEmail" placeholder="you@example.com" />
            </div>
            <div class="field">
              <label>Phone</label>
              <input type="tel" class="text" v-model="voterPhone" placeholder="e.g., +1 555 123 4567" />
            </div>
            <div class="field">
              <label>Reason (optional)</label>
              <input type="text" class="text" v-model="voterReason" placeholder="Why are you voting this way?" />
            </div>
            <div v-if="!voterInfoValid" class="vote-error">Name, a valid email, and phone are required to vote.</div>
          </div>

          <div class="vote-options">
            <button 
              class="vote-btn for"
              :class="{ selected: votedOption === 'for' }"
              @click="voteFor"
              :disabled="isVoting || hasVoted || !voterInfoValid"
            >
              ✓ Vote For
            </button>
            <button 
              class="vote-btn against"
              :class="{ selected: votedOption === 'against' }"
              @click="voteAgainst"
              :disabled="isVoting || hasVoted || !voterInfoValid"
            >
              ✗ Vote Against
            </button>
            <button 
              class="vote-btn abstain"
              :class="{ selected: votedOption === 'abstain' }"
              @click="voteAbstain"
              :disabled="isVoting || hasVoted || !voterInfoValid"
            >
              — Abstain
            </button>
          </div>

          <div v-if="voteError" class="vote-error">
            {{ voteError }}
          </div>
        </div>

        <div v-else-if="!isBudget" class="completed-notice">
          <p>This proposal has been completed and voting is closed.</p>
        </div>
      </div>

      <div class="modal-footer">
        <div v-if="!isBudget" class="share-wrapper">
          <button class="btn-secondary" @click="copyShareLink">Share</button>
          <span v-if="shareCopied" class="share-tooltip">Link has been copied</span>
        </div>
        <button class="btn-secondary" @click="close">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, toRefs } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'ProposalDetail',
  props: {
    proposal: {
      type: Object,
      required: true
    },
    requireVoterInfo: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'vote'],
  setup(props, { emit }) {
    const store = useStore()
    const { requireVoterInfo } = toRefs(props)
    const isVoting = ref(false)
    const voteError = ref('')

    // Optional voter info (for public view), required if requireVoterInfo=true
    const voterName = ref('')
    const voterEmail = ref('')
    const voterPhone = ref('')
    const voterReason = ref('')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const phoneRegex = /^\+?[0-9\s\-()]{7,}$/

    const voterInfoValid = computed(() => {
      if (!props.requireVoterInfo) return true
      const nameOk = voterName.value.trim().length > 0
      const emailOk = emailRegex.test(voterEmail.value.trim())
      const phoneOk = phoneRegex.test(voterPhone.value.trim())
      return nameOk && emailOk && phoneOk
    })

    const shareCopied = ref(false)

    const voteTally = computed(() => 
      props.proposal?.vote_tally || { for: 0, against: 0, abstain: 0 }
    )

    const isBudget = computed(() => {
      const t = (props.proposal?.proposal_type || '').toString().toLowerCase()
      const tid = (props.proposal?.proposal_type_id || '').toString().toLowerCase()
      return t === 'budget' || tid === 'budget'
    })

    const budgetData = computed(() => props.proposal?.proposal_data || props.proposal?.data || {})

    const formatAmount = (val) => {
      const n = Number(val)
      if (!Number.isFinite(n)) return '-'
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    // Build public share URL based on metadata.public_url last segment (poll id)
    const pollId = computed(() => {
      const pub = props.proposal?.metadata?.public_url || ''
      try {
        const url = new URL(pub)
        const segments = url.pathname.split('/').filter(Boolean)
        return segments.pop() || props.proposal?.id || ''
      } catch (_) {
        // Not a full URL; try splitting the string path
        if (typeof pub === 'string' && pub.includes('/')) {
          const segments = pub.split('/').filter(Boolean)
          return segments.pop() || props.proposal?.id || ''
        }
        return props.proposal?.id || ''
      }
    })

    const shareUrl = computed(() => `${window.location.origin}/governance/polls/${pollId.value}`)

    const creatorName = computed(() => 
      props.proposal?.metadata?.creator_name || props.proposal?.created_by_name || props.proposal?.created_by
    )

    // User vote can come from metadata.user_vote (new) or top-level user_vote (legacy)
    const userVote = computed(() => props.proposal?.metadata?.user_vote || props.proposal?.user_vote || null)

    const hasVoted = computed(() => {
      if (props.proposal?.metadata && typeof props.proposal.metadata.has_voted === 'boolean') {
        return props.proposal.metadata.has_voted
      }
      // Fallback: if we have a userVote object, consider the user as having voted
      return !!userVote.value
    })

    const votedOption = computed(() => {
      return props.proposal?.metadata?.voted_option || userVote.value?.vote_option || ''
    })

    // Normalize counts from either legacy shape {for, against, abstain}
    // or new shape { votes_by_option: { for, against, abstain }, participated_power }
    const optionCounts = computed(() => {
      const vt = voteTally.value || {}
      if (vt && vt.votes_by_option) {
        return {
          for: Number(vt.votes_by_option.for || 0),
          against: Number(vt.votes_by_option.against || 0),
          abstain: Number(vt.votes_by_option.abstain || 0)
        }
      }
      return {
        for: Number(vt.for || 0),
        against: Number(vt.against || 0),
        abstain: Number(vt.abstain || 0)
      }
    })

    const totalParticipation = computed(() => {
      const vt = voteTally.value || {}
      const power = Number(vt.participated_power)
      if (Number.isFinite(power) && power > 0) return power
      const c = optionCounts.value
      return (c.for || 0) + (c.against || 0) + (c.abstain || 0)
    })

    const getOptionCount = (option) => Number(optionCounts.value[option] || 0)

    const getTallyPercentage = (option) => {
      const total = totalParticipation.value || 0
      if (!total) return 0
      const count = getOptionCount(option)
      const pct = Math.round((count / total) * 100)
      return Number.isFinite(pct) ? pct : 0
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
    }

    const formatVoteOption = (option) => {
      const map = {
        'for': 'For',
        'against': 'Against',
        'abstain': 'Abstain'
      }
      return map[option] || option
    }

    const castVote = async (voteOption, reason = '') => {
      isVoting.value = true
      voteError.value = ''
      
      try {
        if (props.requireVoterInfo) {
          // Public vote flow
          const payload = {
            name: voterName.value.trim(),
            email: voterEmail.value.trim(),
            phone: voterPhone.value.trim(),
            vote_option: voteOption
          }
          if (reason && reason.trim()) payload.reason = reason.trim()
          await (await import('@/services/governanceService')).governanceService.votePublicPoll(pollId.value, payload)
        } else {
          // Authenticated internal vote flow
          await store.dispatch('governance/voteOnProposal', {
            proposalId: props.proposal.id,
            voteOption,
            reason
          })
        }
        emit('vote')
      } catch (error) {
        voteError.value = error.message || 'Failed to cast vote'
      } finally {
        isVoting.value = false
      }
    }

    const voteFor = () => castVote('for', voterReason.value)
    const voteAgainst = () => castVote('against', voterReason.value)
    const voteAbstain = () => castVote('abstain', voterReason.value)

    const close = () => {
      emit('close')
    }

    const copyShareLink = async () => {
      try {
        const text = shareUrl.value
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const el = document.createElement('textarea')
          el.value = text
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        // Show tooltip feedback
        shareCopied.value = true
        setTimeout(() => { shareCopied.value = false }, 1600)
        // Optional global toast
        store.dispatch('showSuccess', 'Share link copied to clipboard', { root: true })
      } catch (e) {
        console.error('Failed to copy link', e)
      }
    }

    return {
      voteTally,
      userVote,
      isVoting,
      voteError,
      creatorName,
      hasVoted,
      votedOption,
      isBudget,
      // voter info
      requireVoterInfo,
      voterName,
      voterEmail,
      voterPhone,
      voterReason,
      voterInfoValid,
      // share state
      shareCopied,
      // budget details
      budgetData,
      // functions
      getTallyPercentage,
      getOptionCount,
      formatDate,
      formatVoteOption,
      voteFor,
      voteAgainst,
      voteAbstain,
      close,
      shareUrl,
      copyShareLink,
      formatAmount
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.modal-close:hover {
  color: #111827;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid #e9ecef;
}

.type-badge {
  flex-shrink: 0;
  width: 70px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  color: white;
}

.type-badge.poll {
  background: #3b82f6;
}

.type-badge.budget {
  background: #f59e0b;
}

.type-badge.decision {
  background: #8b5cf6;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  flex: 1;
}

.modal-body {
  padding: 24px;
}

.section {
  margin-bottom: 24px;
}

.section h3 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section p {
  margin: 0;
  font-size: 14px;
  color: #37352f;
  line-height: 1.6;
}

.metadata {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f9fafb;
  border-radius: 6px;
  padding: 12px;
  font-size: 13px;
  margin-bottom: 20px;
}

.meta-row {
  display: flex;
  gap: 12px;
}

.meta-row .label {
  font-weight: 500;
  color: #6c757d;
  min-width: 100px;
}

.meta-row .value {
  color: #37352f;
  flex: 1;
}

.meta-row .value.status {
  padding: 2px 8px;
  border-radius: 3px;
  font-weight: 500;
}

.meta-row .value.status.active {
  background: #dcfce7;
  color: #166534;
}

.meta-row .value.status.completed {
  background: #e5e7eb;
  color: #6b7280;
}

.vote-tally {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tally-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tally-label {
  font-weight: 500;
  color: #37352f;
  min-width: 70px;
  font-size: 13px;
}

.tally-count {
  font-weight: 600;
  font-size: 16px;
  color: #111827;
  min-width: 30px;
  text-align: right;
}

.tally-bar {
  flex: 1;
  height: 24px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.tally-fill {
  height: 100%;
  width: 0%; /* start at 0% */
  font-size: 11px;
  font-weight: 600;
  color: white;
  transition: width 0.6s ease-in-out;
}

.tally-percent {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #111827;
  pointer-events: none;
}

.tally-fill.for {
  background: #10b981;
}

.tally-fill.against {
  background: #ef4444;
}

.tally-fill.abstain {
  background: #9ca3af;
}

.voting-section {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 16px;
}

.user-vote-info {
  background: #dcfce7;
  border: 1px solid #86efac;
  border-radius: 4px;
  padding: 12px;
  font-size: 13px;
  color: #166534;
}

.user-vote-info p {
  margin: 0;
}

.vote-reason {
  margin-top: 4px;
  font-style: italic;
  opacity: 0.9;
}

.vote-options {
  display: flex;
  gap: 8px;
}

.vote-btn {
  flex: 1;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.vote-btn.for {
  background: #dcfce7;
  color: #166534;
}

.vote-btn.for:hover:not(:disabled) {
  background: #bbf7d0;
}

.vote-btn.against {
  background: #fee;
  color: #c00;
}

.vote-btn.against:hover:not(:disabled) {
  background: #fdd;
}

.vote-btn.abstain {
  background: #e5e7eb;
  color: #6b7280;
}

.vote-btn.abstain:hover:not(:disabled) {
  background: #d1d5db;
}

/* Highlight selected (voted) option */
.vote-btn.selected.for { border: 2px solid #166534; }
.vote-btn.selected.against { border: 2px solid #c00; }
.vote-btn.selected.abstain { border: 2px solid #6b7280; }

.vote-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.vote-error {
  margin-top: 8px;
  padding: 8px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c00;
  font-size: 12px;
}

/* Voter info fields for public voting */
.voter-info { display: grid; gap: 8px; margin: 12px 0; }
.voter-info .field { display: grid; gap: 4px; }
.voter-info label { font-size: 12px; color: #6b7280; }
.voter-info .text { padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; }
.voter-info .text:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102,126,234,0.1); }

.completed-notice {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #6b7280;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.share-wrapper { position: relative; display: inline-block; }
.share-tooltip {
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}

.btn-secondary {
  padding: 8px 16px;
  background: #f3f4f6;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 13px;
  color: #37352f;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #e9ecef;
}
</style>
